// /src/components/LeadFormModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { trackEventDedup, persistLeadData } from '../lib/tracking';
import { useT } from '../i18n/LanguageContext';

const LeadFormModal = ({ open, onClose, source }) => {
  const { t } = useT();
  const m = t.modal;

  const [form, setForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const startedRef = useRef(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setErrorMsg('');
      setSubmitting(false);
      startedRef.current = false;
      // FormView (no fbq dedup necessary)
      trackEventDedup('FormView', { source: source || 'unknown' });
    } else {
      setForm({ nome: '', cognome: '', email: '', telefono: '', privacy: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = field === 'privacy' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    if (!startedRef.current) {
      startedRef.current = true;
      trackEventDedup('FormStart', { source: source || 'unknown', first_field: field });
    }
  };

  const validate = () => {
    if (!form.nome.trim()) return m.err_nome;
    if (!form.cognome.trim()) return m.err_cognome;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) return m.err_email;
    const cleanPhone = form.telefono.replace(/[\s().-]/g, '');
    const phoneOk = /^\+[1-9]\d{6,14}$/.test(cleanPhone);
    if (!phoneOk) return m.err_phone;
    if (!form.privacy) return m.err_privacy;
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    setErrorMsg('');
    setSubmitting(true);

    const leadPayload = {
      first_name: form.nome.trim(),
      last_name: form.cognome.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.telefono.replace(/[\s().-]/g, ''),
      privacy_consent: form.privacy,
      source: source || 'unknown',
      offer: '2,50€ di sconto',
    };

    try {
      await trackEventDedup('Lead', leadPayload, {
        currency: 'EUR',
        value: 2.5,
        content_name: '2,50€ di sconto',
      });
      persistLeadData(leadPayload);
      await trackEventDedup('PageView', {
        context: 'post_lead_submit',
        source: source || 'unknown',
      });

      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'sfarina_lead_submit',
          lead_source: source || 'unknown',
        });
      }
      setSuccess(true);
    } catch (e2) {
      setErrorMsg(m.err_generic);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lead-form-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-modal-title"
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        ref={dialogRef}
        className="relative bg-[#FDFBF7] w-full max-w-lg rounded-2xl shadow-2xl border border-[#EDE8E0] overflow-hidden animate-[fadeIn_.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="lead-form-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white hover:bg-[#EDE8E0] border border-[#EDE8E0] flex items-center justify-center transition-colors"
        >
          <X size={18} className="text-[#1A1A1A]" />
        </button>

        <div className="bg-[#b03121] text-white px-6 py-7">
          <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.2em] opacity-90 mb-2">
            {m.kicker}
          </p>
          <h3 id="lead-modal-title" className="font-['Poppins',_sans-serif] font-black text-2xl md:text-3xl leading-tight">
            {m.title_line1}
            <br />
            <span className="text-[#FDFBF7]/90 italic font-bold">{m.title_line2}</span>
          </h3>
        </div>

        <div className="p-6 md:p-8">
          {success ? (
            <div data-testid="lead-form-success" className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#b03121]/10 border-2 border-[#b03121] flex items-center justify-center mb-5">
                <svg viewBox="0 0 24 24" fill="none" stroke="#b03121" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h4 className="font-['Poppins',_sans-serif] font-bold text-2xl text-[#1A1A1A] mb-3">
                {m.success_title} {form.nome}! 🎉
              </h4>
              <p className="font-['Lora',_serif] text-[#333] text-base leading-relaxed mb-6">
                {m.success_body_pre}
                <span className="font-bold text-[#b03121]">{m.success_body_h}</span>
                {m.success_body_post}
                <br />
                {m.success_body_2}
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="lead-form-success-close"
                className="inline-flex items-center justify-center bg-[#1A1A1A] hover:bg-[#333] text-white font-['Poppins',_sans-serif] font-bold py-3 px-8 rounded-lg transition-colors"
              >
                {m.success_close}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="lead-form" noValidate>
              <p className="font-['Lora',_serif] text-[#5a4a42] text-sm md:text-base mb-5 leading-relaxed">
                {m.subtitle}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="lead-nome" className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">{m.labels.nome}</label>
                  <input id="lead-nome" data-testid="lead-input-nome" type="text" value={form.nome} onChange={handleChange('nome')} autoComplete="given-name" required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[#b03121] focus:ring-2 focus:ring-[#b03121]/20 outline-none font-['Lora',_serif] text-[#1A1A1A]" />
                </div>
                <div>
                  <label htmlFor="lead-cognome" className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">{m.labels.cognome}</label>
                  <input id="lead-cognome" data-testid="lead-input-cognome" type="text" value={form.cognome} onChange={handleChange('cognome')} autoComplete="family-name" required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[#b03121] focus:ring-2 focus:ring-[#b03121]/20 outline-none font-['Lora',_serif] text-[#1A1A1A]" />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="lead-email" className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">{m.labels.email}</label>
                <input id="lead-email" data-testid="lead-input-email" type="email" value={form.email} onChange={handleChange('email')} autoComplete="email" required
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[#b03121] focus:ring-2 focus:ring-[#b03121]/20 outline-none font-['Lora',_serif] text-[#1A1A1A]" />
              </div>
              <div className="mt-4">
                <label htmlFor="lead-telefono" className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">{m.labels.telefono}</label>
                <input id="lead-telefono" data-testid="lead-input-telefono" type="tel" inputMode="tel" value={form.telefono} onChange={handleChange('telefono')} autoComplete="tel" placeholder="+393331234567" pattern="^\+[1-9][0-9\s().-]{6,}$" required
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[#b03121] focus:ring-2 focus:ring-[#b03121]/20 outline-none font-['Lora',_serif] text-[#1A1A1A]" />
                <p className="text-xs text-[#5a4a42] mt-1.5 font-['Lora',_serif]">
                  {m.phone_hint_pre}<span className="font-semibold">{m.phone_hint_strong}</span>{m.phone_hint_post}
                </p>
              </div>
              <label className="flex items-start gap-3 mt-5 cursor-pointer">
                <input type="checkbox" data-testid="lead-input-privacy" checked={form.privacy} onChange={handleChange('privacy')} className="mt-1 w-5 h-5 accent-[#b03121] cursor-pointer" />
                <span className="font-['Lora',_serif] text-xs md:text-sm text-[#5a4a42] leading-snug">
                  {m.privacy}{' '}
                  <a href="#" className="underline hover:text-[#b03121]">{t.footer.privacy}</a>.
                </span>
              </label>
              {errorMsg && (
                <div data-testid="lead-form-error" className="mt-4 px-4 py-3 rounded-lg bg-[#b03121]/10 border border-[#b03121]/30 text-[#b03121] font-['Poppins',_sans-serif] text-sm font-semibold">
                  {errorMsg}
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="lead-form-submit"
                className="mt-6 w-full inline-flex items-center justify-center bg-[#b03121] hover:bg-[#8e261a] disabled:bg-[#b03121]/60 disabled:cursor-not-allowed text-white font-['Poppins',_sans-serif] font-bold py-4 px-8 rounded-lg shadow-lg transition-all">
                {submitting ? m.submitting : m.submit}
              </button>
              <p className="mt-4 text-center text-xs text-[#5a4a42] font-['Lora',_serif]">{m.footer_note}</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadFormModal;
